import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement, forwardRef } from 'react';
import { formatIban, formatMobile, formatPostalCode, formatTehranTime, formatToman, ibanBankName, maskBankCard, maskMobile, maskNationalId, normalizeNationalId, toE164Mobile, toPersianDigits, } from '@bookora/shared';
/** The class that carries `unicode-bidi: isolate`. Defined in `styles/ui.css`. */
export const BIDI_ISOLATE_CLASS = 'bk-bidi';
/** The class that carries the numeric font stack and `tnum`. From the design tokens. */
export const NUMERIC_FONT_CLASS = 'bk-numeric';
/**
 * A bidi-isolated numeric run.
 *
 * ```tsx
 * <Numeric>{formatMobile(patient.phone)}</Numeric>          // ۰۹۱۲ ۱۲۳ ۴۵ ۶۷
 * <Numeric dir="auto">{formatToman(rials)}</Numeric>        // ۱٬۲۵۰٬۰۰۰ تومان
 * ```
 *
 * ── REF AND DOM PROPS ARE FORWARDED ─────────────────────────────────────────
 *
 * It used to build its element from a fixed prop list and forward neither a
 * `ref` nor event handlers. A tooltip trigger is found through its ref and
 * opened through the `onPointerEnter` it clones onto the child, so every value
 * tooltip wrapped around a `Numeric` — `Money short`, `JalaliDate`,
 * `RelativeTime` — could never open, and nothing said so. Every other DOM prop
 * (handlers, `data-*`, `tabIndex`, `aria-*`) is spread FIRST, so the fixed
 * props below — the isolate and its base direction — cannot be overridden by
 * accident.
 */
function NumericImpl({ children, dir = 'ltr', as = 'bdi', tabular = true, className, style, title, dateTime, id, 'aria-label': ariaLabel, 'aria-hidden': ariaHidden, ...rest }, ref) {
    const classes = [BIDI_ISOLATE_CLASS, tabular ? NUMERIC_FONT_CLASS : null, className]
        .filter(Boolean)
        .join(' ');
    return createElement(as, {
        ...rest,
        ref,
        dir,
        className: classes,
        style,
        title,
        id,
        'aria-label': ariaLabel,
        'aria-hidden': ariaHidden,
        ...(as === 'time' && dateTime ? { dateTime } : null),
    }, children);
}
export const Numeric = forwardRef(NumericImpl);
Numeric.displayName = 'Numeric';
/** An Iranian mobile number, grouped and isolated. */
export function Phone({ value, variant = 'grouped', link = false, fallback = '—', className, }) {
    const raw = typeof value === 'string' ? value : '';
    const grouped = formatMobile(raw);
    if (grouped === '')
        return _jsx("span", { className: className, children: fallback });
    const body = variant === 'masked'
        ? maskMobile(raw)
        : variant === 'plain'
            ? grouped.replace(/\s/g, '')
            : grouped;
    const node = _jsx(Numeric, { className: className, children: body });
    if (!link || variant === 'masked')
        return node;
    // The visible text is Persian; the href must not be. `tel:` is parsed by the
    // dialer, and no dialer on earth understands U+06F0. `toE164Mobile` builds it
    // from the CANONICAL value — stripping non-digits out of the formatted string
    // would strip the Persian digits along with the spaces, because `\D` is
    // "not [0-9]" and a Persian digit is not [0-9]. That leaves `tel:+98`.
    const e164 = toE164Mobile(raw);
    if (e164 === null)
        return node;
    return (_jsx("a", { href: `tel:${e164}`, dir: "ltr", children: node }));
}
/** کد ملی — ten digits, or the masked form. */
export function NationalId({ value, masked = false, fallback = '—', className, }) {
    const normalized = normalizeNationalId(value ?? '');
    if (normalized === null)
        return _jsx("span", { className: className, children: fallback });
    return (_jsx(Numeric, { className: className, children: masked ? maskNationalId(normalized) : toPersianDigits(normalized) }));
}
/**
 * کد پستی — «۱۲۳۴۵-۶۷۸۹۰».
 *
 * The hyphen is the trap here and it is a subtle one: U+002D is bidi class ES,
 * and rule W4 folds an ES into a number only when both neighbours are EUROPEAN
 * numbers. Between two ARABIC numbers it survives to W6, becomes a neutral, and
 * the two halves of the code swap.
 */
export function PostalCode({ value, fallback = '—', className }) {
    const body = formatPostalCode(value ?? '');
    if (body === '')
        return _jsx("span", { className: className, children: fallback });
    return _jsx(Numeric, { className: className, children: body });
}
/**
 * شبا — `IR06 2960 0000 0010 0324 2000 01`.
 *
 * Kept in Latin digits on purpose: a شبا is copied into a banking form, and a
 * Persian-digit paste is rejected by every one of them.
 */
export function Iban({ value, withBank = false, fallback = '—', className }) {
    const body = formatIban(value ?? '');
    if (body === '')
        return _jsx("span", { className: className, children: fallback });
    const bank = withBank ? ibanBankName(value ?? '') : '';
    const number = _jsx(Numeric, { className: className, children: body });
    if (bank === '')
        return number;
    return (_jsxs("span", { className: className, children: [number, " ", _jsx("span", { children: bank })] }));
}
/** «۶۱۰۴ ۳۳۷۷ **** ۱۲۳۴». Three space-separated groups and a run of asterisks. */
export function BankCard({ value, fallback = '—', className }) {
    const body = maskBankCard(value ?? '');
    if (body === '')
        return _jsx("span", { className: className, children: fallback });
    return _jsx(Numeric, { className: className, children: body });
}
/** An opaque identifier: isolated, LTR, never reordered by the sentence around it. */
export function Code({ value, persianDigits = false, mono = false, fallback = '—', className, }) {
    if (value === null || value === undefined || value === '') {
        return _jsx("span", { className: className, children: fallback });
    }
    const text = String(value);
    return (_jsx(Numeric, { as: mono ? 'code' : 'bdi', tabular: !mono, className: [mono ? 'bk-mono' : null, className].filter(Boolean).join(' ') || undefined, children: persianDigits ? toPersianDigits(text) : text }));
}
/* ========================================================================== */
/*  Ranges                                                                    */
/* ========================================================================== */
/** U+2013 EN DASH. Bidi class ON — a neutral, and therefore a group-flipper. */
export const RANGE_DASH = '–';
/**
 * «۱–۲۰», «۰۸:۰۰–۱۶:۳۰», «۱۴۰۳/۰۱/۰۱–۱۴۰۳/۱۲/۲۹».
 *
 * A range written with a dash is two numbers with a neutral between them, which
 * is the same shape as the phone bug: without isolation «۱–۲۰» is laid out with
 * the ۲۰ on the left. Ranges written with the word «تا» do not have the problem
 * — «تا» is strong RTL — but they are also two components' worth of markup at
 * every call site, so this covers both by isolating the whole thing. Forwards
 * its ref and DOM props like `Numeric`, so it can be a tooltip trigger.
 */
export const NumericRange = forwardRef(function NumericRange({ from, to, separator = RANGE_DASH, className, title, id, ...rest }, ref) {
    return (_jsxs(Numeric, { ...rest, ref: ref, className: className, title: title, id: id, children: [from, separator, to] }));
});
function coerceDate(value) {
    if (value === null || value === undefined || value === '')
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/** «۰۹:۳۰–۱۰:۰۰» on Tehran's clock — an appointment slot. */
export function TimeRange({ start, end, fallback = '—', className }) {
    const from = coerceDate(start);
    const to = coerceDate(end);
    if (!from || !to)
        return _jsx("span", { className: className, children: fallback });
    return (_jsxs(Numeric, { className: className, children: [_jsx("time", { dateTime: from.toISOString(), children: formatTehranTime(from) }), RANGE_DASH, _jsx("time", { dateTime: to.toISOString(), children: formatTehranTime(to) })] }));
}
/**
 * «از ۲٬۵۰۰٬۰۰۰ تا ۴٬۰۰۰٬۰۰۰ تومان».
 *
 * Deliberately worded rather than dashed. The two figures are each isolated so
 * neither can be reordered, and the Persian scaffolding around them stays RTL
 * where it belongs — which is why the outer element is `dir="auto"` and not
 * `ltr`.
 */
export function PriceRange({ minRials, maxRials, withUnit = true, fallback = '—', className, }) {
    const min = Number.isFinite(minRials) ? minRials : null;
    const max = Number.isFinite(maxRials) ? maxRials : null;
    if (min === null && max === null)
        return _jsx("span", { className: className, children: fallback });
    const low = Math.min(min ?? max, max ?? min);
    const high = Math.max(min ?? max, max ?? min);
    const unit = withUnit ? ' تومان' : '';
    if (low === high) {
        return (_jsxs(Numeric, { dir: "auto", className: className, children: [formatToman(low, { withUnit: false }), unit] }));
    }
    return (_jsxs(Numeric, { dir: "auto", className: className, children: ["\u0627\u0632 ", _jsx(Numeric, { children: formatToman(low, { withUnit: false }) }), " \u062A\u0627", ' ', _jsx(Numeric, { children: formatToman(high, { withUnit: false }) }), unit] }));
}
//# sourceMappingURL=bidi.js.map